/**
 * Bezier symmetry snap + auto-smooth — bezier-snap-touch-brief §1.2, §3–6.
 *
 * Pure geometry, unit-tested in tests/inkSnap.test.ts. The ink editor consumes
 * these; rendering reads the resulting handle vectors.
 *
 * Snap: when a new anchor lands near the *symmetry-equivalent* position of an
 * existing anchor (a copy of it under the tiling), the new anchor inherits that
 * anchor's handles transformed through the same isometry — giving C1 curve
 * continuity across tile boundaries by construction (§6). Handles are the
 * symmetric (in = −out) model the ink editor already uses, which is closed under
 * every wallpaper isometry, so a single handle vector transforms cleanly.
 */
import { apply, compose, invert, isOrientationReversing, transformToMatrix, type Mat } from '@/geometry/affine';
import type { SymmetryVariant } from '@/patterns/types';

/** Screen-space snap radius (brief §4.3) — converted to tile-space at the call
 *  site. Sized for a fingertip on iPad, not a desktop mouse (§1); the edit-mode
 *  node/handle hit radius is 18px, and the snap catch should be at least as
 *  generous. Tunable on-device. */
export const SNAP_PX = 12;

/** A 2D vector through a matrix's linear part only (ignores translation). */
export function applyLinear(m: Mat, hx: number, hy: number): [number, number] {
  return [m[0] * hx + m[2] * hy, m[1] * hx + m[3] * hy];
}

export interface AnchorLike {
  x: number;
  y: number;
  /** in-handle offset (control arriving from prev = point + in) */
  inX: number;
  inY: number;
  /** out-handle offset (control leaving toward next = point + out) */
  outX: number;
  outY: number;
  /** identity of the source node, so the editor can build a persistent link */
  layerId?: string;
  nodeId?: string;
}

export interface SnapHit {
  /** snapped anchor position (exact symmetry-equivalent point) */
  x: number;
  /** inherited in-handle, transformed through the symmetry op */
  y: number;
  inX: number;
  inY: number;
  /** inherited out-handle, transformed through the symmetry op */
  outX: number;
  outY: number;
  /** the existing anchor driving the inheritance (for the highlight) */
  sourceX: number;
  sourceY: number;
  /** identity of the source node (cross-layer linking) */
  sourceLayerId?: string;
  sourceNodeId?: string;
  /** affine mapping the source node onto the snapped position (the link transform) */
  matrix: Mat;
}

/**
 * Find the nearest symmetry-equivalent copy of any existing anchor to the point
 * (px, py), within `thresholdTile`. Returns the snapped position + inherited
 * handle, or null when nothing qualifies.
 *
 * The match must be a *different* point than the source anchor (brief §4.1.2) —
 * a coincident point is the user closing/rejoining a path, not a boundary twin.
 */
export function computeSnap(
  variant: SymmetryVariant,
  existing: readonly AnchorLike[],
  px: number,
  py: number,
  thresholdTile: number,
  rings = 2,
): SnapHit | null {
  if (existing.length === 0 || thresholdTile <= 0) return null;
  const transforms = variant.transformsInRange(rings);
  let best: SnapHit | null = null;
  let bestD = thresholdTile;

  for (const e of existing) {
    // Express the source anchor + its handles in the base tile, then walk its
    // orbit. rep maps the base-tile representative back onto the anchor.
    const rep = variant.wrapPoint(e.x, e.y);
    const repMat = transformToMatrix(rep.transform);
    const repInv = invert(repMat);

    for (const t of transforms) {
      const m = transformToMatrix(t);
      const [cx, cy] = apply(m, [rep.x, rep.y]);
      if (Math.hypot(cx - e.x, cy - e.y) < 1e-6) continue; // coincident — skip
      const d = Math.hypot(cx - px, cy - py);
      if (d < bestD) {
        // link transform: source anchor → snapped copy = m ∘ repMat⁻¹.
        const matrix = compose(m, repInv);
        const [tInX, tInY] = applyLinear(matrix, e.inX, e.inY);
        const [tOutX, tOutY] = applyLinear(matrix, e.outX, e.outY);
        // A reflection/glide reverses traversal, so the in/out roles swap on the
        // copy (matches the store's link propagation).
        const rev = isOrientationReversing(matrix);
        best = {
          x: cx,
          y: cy,
          inX: rev ? tOutX : tInX,
          inY: rev ? tOutY : tInY,
          outX: rev ? tInX : tOutX,
          outY: rev ? tInY : tOutY,
          sourceX: e.x,
          sourceY: e.y,
          sourceLayerId: e.layerId,
          sourceNodeId: e.nodeId,
          matrix,
        };
        bestD = d;
      }
    }
  }
  return best;
}

/**
 * Expand every anchor through the group orbit within `rings`, producing one
 * SnapHit-shaped candidate per (anchor, transform) pair — the single shared
 * candidate set for draw-time rendering (the always-on '+' markers), the
 * hover snap preview, and placement snapping. All three used to call
 * different code with different ring counts (markers/placement used the
 * viewport's full ring count; the hover preview silently defaulted to 2),
 * so a marker could be shown with nothing there to actually snap to.
 * Deliberately does NOT deduplicate by position — a plain lattice anchor
 * and a transformed copy of an existing node can legitimately coincide at
 * the same point, and collapsing them by position would silently discard
 * whichever candidate carries the richer identity (a node-derived
 * candidate drives handle inheritance + cross-layer linking; a plain
 * anchor is position-only — see nearestSnapCandidate, which is where that
 * choice actually gets made, once, consistently, rather than by whichever
 * candidate happened to be generated/deduplicated first).
 *
 * Deliberately separate from computeSnap above, which close-time link
 * resolution depends on unchanged: a NODE anchor's own untransformed
 * position is excluded here (tapping there means closing/rejoining the
 * path in progress, not snapping to a boundary twin — brief §4.1.2), but a
 * PLAIN anchor (a grid/cell-corner anchor, no node identity) has no such
 * meaning at its own base position, so — unlike computeSnap's unconditional
 * exclusion — it stays a valid, ordinary candidate there. Without this
 * distinction, a lattice point that's only reachable as some anchor's own
 * base position (not as anyone's transformed copy) would show a marker but
 * never actually snap.
 */
export function snapCandidates(variant: SymmetryVariant, anchors: readonly AnchorLike[], rings: number): SnapHit[] {
  const transforms = variant.transformsInRange(rings);
  const out: SnapHit[] = [];
  for (const a of anchors) {
    const rep = variant.wrapPoint(a.x, a.y);
    const repMat = transformToMatrix(rep.transform);
    const repInv = invert(repMat);

    for (const t of transforms) {
      const m = transformToMatrix(t);
      const [cx, cy] = apply(m, [rep.x, rep.y]);
      if (a.nodeId !== undefined && Math.hypot(cx - a.x, cy - a.y) < 1e-6) continue;
      const matrix = compose(m, repInv);
      const [tInX, tInY] = applyLinear(matrix, a.inX, a.inY);
      const [tOutX, tOutY] = applyLinear(matrix, a.outX, a.outY);
      const rev = isOrientationReversing(matrix);
      out.push({
        x: cx,
        y: cy,
        inX: rev ? tOutX : tInX,
        inY: rev ? tOutY : tInY,
        outX: rev ? tInX : tOutX,
        outY: rev ? tInY : tOutY,
        sourceX: a.x,
        sourceY: a.y,
        sourceLayerId: a.layerId,
        sourceNodeId: a.nodeId,
        matrix,
      });
    }
  }
  return out;
}

/**
 * Nearest candidate to (px, py) within `thresholdTile`. When two candidates
 * land within floating-point tolerance of the same distance, prefers a
 * node-derived one over a plain lattice anchor (rather than whichever was
 * generated first) — a transformed copy of an existing node that happens to
 * coincide with a grid intersection must still behave as a node snap, not
 * lose its identity to the anchor it landed on.
 */
export function nearestSnapCandidate(
  candidates: readonly SnapHit[],
  px: number,
  py: number,
  thresholdTile: number,
): SnapHit | null {
  const TIE_EPS = 1e-6;
  let best: SnapHit | null = null;
  let bestD = thresholdTile;
  for (const c of candidates) {
    const d = Math.hypot(c.x - px, c.y - py);
    if (d > bestD) continue;
    const isTie = best !== null && Math.abs(d - bestD) <= TIE_EPS;
    const richerOnTie = isTie && !!c.sourceNodeId && !best?.sourceNodeId;
    if (best === null || d < bestD - TIE_EPS || richerOnTie) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

/**
 * Catmull-Rom-style smooth handles for a tapped point sequence (brief §1.2).
 * Each interior anchor's tangent is proportional to (next − prev); endpoints use
 * the one available neighbour. Returns one out-handle vector per point.
 */
export function autoSmoothHandles(
  pts: readonly { x: number; y: number }[],
  closed: boolean,
  k = 1 / 6,
): [number, number][] {
  const n = pts.length;
  return pts.map((p, i) => {
    const prev = closed ? pts[(i - 1 + n) % n] : pts[i - 1];
    const next = closed ? pts[(i + 1) % n] : pts[i + 1];
    if (prev && next) return [(next.x - prev.x) * k, (next.y - prev.y) * k];
    if (next) return [(next.x - p.x) * k, (next.y - p.y) * k];
    if (prev) return [(p.x - prev.x) * k, (p.y - prev.y) * k];
    return [0, 0];
  });
}
